import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import config from '../../config';
import axios from 'axios';
import { useGlobalContext } from '../../GlobalContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { GetById, GetBatchStudentsByBatchId } from '../../service/BatchService';
import { GetBatchStudentPayments } from '../../service/BatchStudentService';
import { createRazorpayOrder, UpdatePaymentDetails } from '../../service/BatchStudentPaymentService';

export default function BatchSummary() {
  const {
    isLoading,
    setIsLoading,
    isAppError,
    setAppError,
    appErrorMessage,
    setAppErrorMessage,
    appErrorTitle,
    setAppErrorTitle,
    appErrorMode,
    setAppErrorMode,
    appUser,
  } = useGlobalContext();

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;
  const { id } = useParams();

  const userId = appUser._id; // Get the BatchStudent ID from the appBatchStudent context

  const [payments, setPayments] = useState([]);
  const [batchStudents, setBatchStudents] = useState([]);

  const [batchDetails, setBatchDetails] = useState({});
  const itemsPerPageStudent = 10;

  const fetchBatchDetails = async () => {
    try {
      setIsLoading(true);
      const response = await GetById(id);
      if (response?.batch) {
        setBatchDetails(response.batch);
      } else {
        setBatchDetails({});
      }
    } catch (error) {
      setAppError(true);
      setAppErrorTitle("Error");
      setAppErrorMessage("Failed to fetch batch details");
      setAppErrorMode("error");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBatchStudent = async (page = 1) => {
    try {
      setIsLoading(true);
      const response = await GetBatchStudentPayments(id, userId, page, itemsPerPageStudent);
      console.log("Batch Students Response:", response);
      setBatchStudents(response.payments);
      setCurrentPage(response.currentPage);
      setTotalPages(response.totalPages);
      setTotalItems(response.totalItems);
    } catch (error) {
      setAppError(true);
      setAppErrorTitle("Error");
      setAppErrorMessage("Failed to load data");
      setAppErrorMode("error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBatchDetails();
  }, []);


  React.useEffect(() => {
    fetchBatchStudent(currentPage);
  }, []);


  //razorpay payment gateway integration
  const handlePayment = async (batchStudent) => {
    try {
      const enteredAmount = Number(batchStudent.amount); // auto from batchstudentpayment

      if (!enteredAmount || isNaN(enteredAmount) || enteredAmount <= 0) {
        setAppError(true);
        setAppErrorTitle("Error");
        setAppErrorMessage("Invalid payment amount.");
        setAppErrorMode("error");
        return;
      }

      setIsLoading(true);

      // 1. Create Razorpay Order
      const orderData = await createRazorpayOrder(enteredAmount);
      if (!orderData?.orderId) throw new Error("Order creation failed");

      // 2. Razorpay Checkout Config
      const options = {
        key: config.razorpayKeyId,
        amount: orderData.amount,
        currency: orderData.currency,
        name: "Payful Pencil",
        description: "Batch Fee Payment",
        order_id: orderData.orderId,
        handler: async function (response) {
          try {
            const razorpay_payment_id = response.razorpay_payment_id;

            // 3. Update existing batchstudentpayment
            const updateRes = await UpdatePaymentDetails(
              batchStudent._id,
              "paid",
              razorpay_payment_id,
              new Date().toISOString()
            );

            if (updateRes.status === 200) {
              setAppError(true);
              setAppErrorTitle("Action Response");
              setAppErrorMessage(updateRes.message || "Payment Successfully Updated");
              setAppErrorMode("success");
              window.location.href = `/batch-student/manage/${id}`;
            } else {
              setAppError(true);
              setAppErrorTitle("Error");
              setAppErrorMessage(updateRes.message || "Update failed. Please try again.");
              setAppErrorMode("error");
            }
          } catch (error) {
            setAppError(true);
            setAppErrorTitle("Error");
            setAppErrorMessage("Something went wrong while updating payment.");
            setAppErrorMode("error");
          } finally {
            setIsLoading(false);
          }
        },
        prefill: {
          name: appUser?.username || "",
          email: appUser?.email || "",
          contact: appUser?.mobile || "",
        },
        notes: {
          batch: batchDetails?.name || "",
          student: batchStudent?.studentName || "",
          email: appUser?.email || "",
          mobile: appUser?.mobile || "",
        },
        theme: {
          color: "#3399cc",
        },
      };

      // 4. Open Razorpay
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      console.error(error);
      setAppError(true);
      setAppErrorTitle("Error");
      setAppErrorMessage("Payment initiation failed.");
      setAppErrorMode("error");
      setIsLoading(false);
    }
  };



  return (
    <>
      <Helmet>
        <title>Fee Summary Details | {config.appName}</title>
      </Helmet>
      <div className='container'>
        <div className='page'>
          <div className='page-heading'>
            <h1>My Fee Summary</h1>
            <span>
              <Link to="/"> Dashboard </Link> / <Link to={`/batch/list`}> Batch List </Link>
              / My Fee Summary
            </span>
          </div>

          <div className='page-content'>
            <div className="portal">
              <div className='portal-body'>
                <div className='row'>
                  <div className='col-lg-12 col-md-12 col-sm-12'>
                    <div className='batch-details'>
                      <div className='row'>
                        <div className='col-lg-3 col-md-3 col-sm-12'>
                          Category : <strong>{batchDetails?.courseId?.categoryId?.name || '-'}</strong>
                        </div>
                        <div className='col-lg-3 col-md-3 col-sm-12'>
                          Sub Category : <strong> {batchDetails?.courseId?.subCategoryIds
                            ?.map(sub => sub.name)
                            .join(", ")}</strong>
                        </div>
                        <div className='col-lg-6 col-md-6 col-sm-12'>
                          Course : <strong>{batchDetails?.courseId?.name || '-'}</strong>
                        </div>
                      </div>
                    </div>
                    <br></br>
                    <div className='batch-details'>
                      <div className='row mb-3'>
                        <div className='col-lg-3 col-md-3 col-sm-12'>
                          Batch Code : <strong>{batchDetails?.code || '-'}</strong>
                        </div>
                        <div className='col-lg-3 col-md-3 col-sm-12'>
                          Start Date : <strong>
                            {batchDetails?.startDate
                              ? (() => {
                                const date = new Date(batchDetails.startDate);
                                const day = String(date.getDate()).padStart(2, '0');
                                const month = String(date.getMonth() + 1).padStart(2, '0');
                                const year = date.getFullYear();
                                return `${day}-${month}-${year}`;
                              })()
                              : '-'}
                          </strong>
                        </div>
                        <div className='col-lg-3 col-md-3 col-sm-12'>
                          Fee : <strong>{batchDetails?.fee}</strong>
                        </div>
                        <div className='col-lg-3 col-md-3 col-sm-12'>
                          Certificate : <strong>
                            {batchDetails?.certificate === true
                              ? "Yes"
                              : batchDetails?.certificate === false
                                ? "No"
                                : "-"}
                          </strong>
                        </div>
                      </div>
                      <div className='row'>
                        <div className='col-lg-6 col-md-6 col-sm-12'>
                          Batch Name : <strong>{batchDetails?.name || '-'}</strong>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <br></br>

                <div className='row'>
                  <div className='col-lg-12 col-md-12 col-xs-12'>
                    <h5><b> <FontAwesomeIcon icon="fa-solid fa-coins" /> Payment Details</b></h5>
                    <div className='table-content'>
                      <table className='table table-bordered table-condensed'>
                        <thead>
                          <tr>
                            <th>Due Date</th>
                            <th>Amount (Rs.)</th>
                            <th> Status </th>

                            <th> Action </th>
                          </tr>
                        </thead>
                        <tbody>
                          {batchStudents && batchStudents.map((batchStudent) => (
                            <tr key={batchStudent._id}>
                              <td>
                                {batchStudent.lastDate
                                  ? (() => {
                                    const date = new Date(batchStudent.lastDate);
                                    const day = String(date.getDate()).padStart(2, '0');
                                    const month = String(date.getMonth() + 1).padStart(2, '0');
                                    const year = date.getFullYear();
                                    return `${day}-${month}-${year}`;
                                  })()
                                  : ''}
                              </td>
                              <td>{batchStudent.amount}</td>
                              <td>{batchStudent.status}</td>
                              <td>
                                {batchStudent.status === 'active' ? (
                                  <button
                                    className="btn btn-primary btn-sm"
                                    onClick={() => handlePayment(batchStudent)}
                                  >
                                    Pay Now
                                  </button>
                                ) : '-' }
                              </td>

                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {totalItems > 10 && (
                        <div className="pagination mt-3 d-flex justify-content-center">
                          <span className="align-self-center me-3">
                            {currentPage > 1 && (
                              <button
                                className="btn btn-outline-primary me-2"
                                onClick={() => fetchBatchStudent(currentPage - 1)}
                              >
                                Previous
                              </button>
                            )}
                            Page {currentPage} of {totalPages}
                            {currentPage < totalPages && (
                              <button
                                className="btn btn-outline-primary ms-2"
                                onClick={() => fetchBatchStudent(currentPage + 1)}
                              >
                                Next
                              </button>
                            )}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div >
    </>
  );
}